import { isEqual, orderBy } from 'lodash';
import * as React from 'react';
import { ResourceProvider } from '../../resourceProvider';
import { CountryAvailability } from './../countryAvailability';

export interface IGeneralAvailabilityProps {
	availability: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.GeneralAvailabilityModel,
	countries: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.CodeListItemModel[],
	families: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.ModelFamilyModel[],
	infotainments: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.InfotaimentModel[],
	settings: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.ImporterSettingsModel
}

export interface IGeneralAvailabilityState {
	countries: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.CodeListItemModel[],
	prevCountries: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.CodeListItemModel[],
	families: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.ModelFamilyModel[],
	infotainments: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.InfotaimentModel[],
	familiesMap: any,
	infotainmentsMap: any
}

export class GeneralAvailability extends React.Component<IGeneralAvailabilityProps, IGeneralAvailabilityState>
{
	constructor(props: IGeneralAvailabilityProps) {
		super(props);

		this.state = {
			countries: props.countries,
			prevCountries: null,
			families: props.families,
			infotainments: props.infotainments,
			familiesMap: null,
			infotainmentsMap: null
		};
	}

	static getDerivedStateFromProps(props: IGeneralAvailabilityProps, state: IGeneralAvailabilityState): IGeneralAvailabilityState {
		let newState = { ...state };
		let hasChange: boolean = false;

		if (!isEqual(props.countries, state.prevCountries)) {
			newState.countries = props.countries;
			newState.prevCountries = props.countries;
			hasChange = true;
		}

		if ((!isEqual(props.families, state.families) || state.familiesMap == null) && (props.families.length > 0 || state.families.length > 0)) {
			let familiesMap: any = {};
			for (var i = 0; i < props.families.length; i++) {
				familiesMap[props.families[i].Code] = props.families[i];
			}
			newState.families = props.families;
			newState.familiesMap = familiesMap;
			hasChange = true;
		}

		if ((!isEqual(props.infotainments, state.infotainments) || state.infotainmentsMap == null) && (props.infotainments.length > 0 || state.infotainments.length > 0)) {
			let infotainmentsMap: any = {};
			for (var i = 0; i < props.infotainments.length; i++) {
				infotainmentsMap[props.infotainments[i].Code] = props.infotainments[i];
			}
			newState.infotainments = props.infotainments;
			newState.infotainmentsMap = infotainmentsMap;
			hasChange = true;
		}

		if (hasChange)
			return newState;
		else
			return null;
	}

	public shouldComponentUpdate(nextProps: IGeneralAvailabilityProps, nextState: IGeneralAvailabilityState): boolean {
		return nextState.countries && nextState.familiesMap && nextState.infotainmentsMap;
	}

	private getFamilyModels(families: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.ServiceAvailableForFamilyModel[]): SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.ModelFamilyModel[] {
		if (!this.state.familiesMap)
			return [];

		let result = [];

		for (var i = 0; i < families.length; i++) {
			result.push(this.state.familiesMap[families[i].FamilyCode]);
		}

		return orderBy(result, ['Title'], ['asc']);
	}

	private renderSortedFamilies(families: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.ServiceAvailableForFamilyModel[]): JSX.Element[] {
		let productionMap: any = {};

		for (var i = 0; i < families.length; i++) {
			productionMap[families[i].FamilyCode] = families[i];
		}

		let models = this.getFamilyModels(families);

		if (models && models.length > 0) {
			return models.map((model) =>
				<div className="row d-block family" key={'family_' + model.Code}>
					{
						this.renderFamily(productionMap[model.Code], model)
					}
				</div>
			);
		} else {
			return null;
		}
	}

	private renderFamily(familyProductions: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.ServiceAvailableForFamilyModel, familyModel: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.ModelFamilyModel): JSX.Element {
		var imageClass = 'new-render';

		return (
			<div key={'familyTitle_' + familyModel.Code}>
				<table>
					<tbody>
						<tr>
							<td className="image-cell" style={{width: "72px"}}>
								<img className={imageClass} src={familyModel.FrontImageUrl} alt={familyModel.Title} />
							</td>
							<td className="title-cell">
								<span className="title">{familyModel.Title}</span>
							</td>
							<td className="productions-cell d-sm-hidden">
								{
									this.renderFamilyProductions(familyProductions)
								}
							</td>
						</tr>
						<tr className="productions-list-row-mobile">
							<td colSpan={2}>
								{
									this.renderFamilyProductions(familyProductions)
								}
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		);
	}

	private renderFamilyProductions(model: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.ServiceAvailableForFamilyModel, classes: string = null): JSX.Element {
		let labels: string[] = [];

		if (model.Productions && model.Productions.length > 0) {
			for (var i = 0; i < model.Productions.length; i++) {
				let label: string = '';
				let from = model.Productions[i].From;
				label = label + ResourceProvider.getText('ServicePage.FromProduction') + ' ' + from.Title;

				let to = model.Productions[i].To;
				if (to) {
					label = label + ' ' + ResourceProvider.getText('ServicePage.ToProduction') + ' ' + to.Title;
				}

				labels.push(label);
			}
		};

		if (!model || !model.Productions)
			return null;

		let renderPeriods = (labels: string[]): JSX.Element[] => {
			let result: JSX.Element[] = [];

			for (var i = 0; i < labels.length; i++) {
				result.push(<span key={'production_' + i} className="productions">{labels[i]}</span>);
				if (i < labels.length - 1) {
					result.push(<br key={'break_' + i} />);
				}
			}

			return result;
		};

		return (
			<div className={classes ? ' ' + classes : ''} key={'familyProduction_' + model.FamilyCode}>
				{
					renderPeriods(labels)
				}
			</div>
		);
	}

	private renderInfotainment(code: string): JSX.Element {
		if (!this.state.infotainmentsMap)
			return null;

		let infotainment: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.InfotaimentModel = this.state.infotainmentsMap[code];

		if (!infotainment)
			return null;

		return (
			<div className="row infotainment" key={'infotainment_' + code}>
				<table>
					<tbody>
						<tr>
							<td className="image-cell">
								<img src={infotainment.Image} alt={infotainment.Title} />
							</td>
							<td className="title-cell">
								<span className="title">
									{infotainment.Title}
								</span>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		);
	}

	public render(): JSX.Element {
		let availableCountries = this.props.availability && this.props.availability.AvailableForCountries
			? this.state.countries.filter((c) => this.props.availability.AvailableForCountries.findIndex((i) => i == c.Code) > -1)
			: [];

		let families = this.props.availability ? this.props.availability.AvailableForFamilies : null;
		let infotainments = this.props.availability ? this.props.availability.AvailableForInfotainments : null;

		return (
			<div className="container general-availability-container">
				<div className="row">
					<h2>{ResourceProvider.getText('GeneralAvailability.Title')}</h2>
				</div>

				<div style={{height: "80px"}} className="row caption d-md-hidden" data-toggle="collapse" data-target="#collapseCountries" aria-expanded="false" aria-controls="collapseCountries">
					<h3>{ResourceProvider.getText('GeneralAvailability.Countries.Title')}</h3>
					<i className="icon icon-arrow-medium-down"></i>
					<i className="icon icon-arrow-medium-up"></i>
				</div>
				<div className="row caption d-sm-hidden">
					<h3>{ResourceProvider.getText('GeneralAvailability.Countries.Title')}</h3>
				</div>
				<CountryAvailability countries={availableCountries} collapsible={true} />

				<div style={{ height: "80px" }} className="row caption d-md-hidden" data-toggle="collapse" data-target="#collapseFamilies" aria-expanded="false" aria-controls="collapseFamilies">
					<h3>{ResourceProvider.getText('GeneralAvailability.Families.Title')}</h3>
					<i className="icon icon-arrow-medium-down"></i>
					<i className="icon icon-arrow-medium-up"></i>
				</div>
				<div className="row caption d-sm-hidden">
					<h3>{ResourceProvider.getText('GeneralAvailability.Families.Title')}</h3>
				</div>
				<div className="w-100 families collapse" id="collapseFamilies">
					{
						families && this.renderSortedFamilies(families)
					}
				</div>

				<div style={{ height: "80px" }} className="row caption d-md-hidden" data-toggle="collapse" data-target="#collapseInfotainments" aria-expanded="false" aria-controls="collapseInfotainments">
					<h3>{ResourceProvider.getText('GeneralAvailability.Infotainments.Title')}</h3>
					<i className="icon icon-arrow-medium-down"></i>
					<i className="icon icon-arrow-medium-up"></i>
				</div>
				<div className="row caption d-sm-hidden">
					<h3>{ResourceProvider.getText('GeneralAvailability.Infotainments.Title')}</h3>
				</div>
				<div className="w-100 infotainments collapse" id="collapseInfotainments">
					{
						infotainments && infotainments.map((infotainment) => this.renderInfotainment(infotainment))
					}
					{
						this.props.settings && this.props.settings.InfotainmentsLink &&
						<div className="row show-more">
							<a href={this.props.settings.InfotainmentsLink} className="undecorated-link" target="_blank">
								<h6>{ResourceProvider.getText('GeneralAvailability.Infotainments.ShowMore')}</h6>
								<span className="icon icon-arrow-medium-right"></span>
							</a>
						</div>
					}
				</div>
			</div>
		);
	}
}